import { Grid, Typography } from "@mui/material";
import { SignalsTemporalLabels } from "components/Signals/SignalsModels";
import moment from "moment";
import type { SignalReportDatum } from "models/Reporting/ReportDatum";
import theme from "Theme/AppTheme";
import { getXDateRangeLabel } from "./dateBucketLabels";

export const getEligibleConversations = (
    datum: SignalReportDatum | undefined,
    totalField: "y_totalcount" | "y_percenttotal" = "y_totalcount",
): { count: number; total: number } | undefined => {
    if (!datum || datum.isSyntheticTemporalGap) return undefined;

    const count = datum.y;
    const total = datum[totalField];
    if (typeof count !== "number" || typeof total !== "number") {
        return undefined;
    }

    return { count, total };
};

export const getScoredConversationCount = (
    datum:
        | Pick<
              SignalReportDatum,
              "y_percent" | "y_percenttotal" | "isSyntheticTemporalGap"
          >
        | undefined,
): number | undefined => {
    if (!datum || datum.isSyntheticTemporalGap) return undefined;

    const { y_percent: score, y_percenttotal: count } = datum;
    if (
        typeof score !== "number" ||
        !Number.isFinite(score) ||
        typeof count !== "number" ||
        !Number.isFinite(count) ||
        count <= 0
    ) {
        return undefined;
    }

    return count;
};

export const dataPointTooltipBuilder = (args: {
    x: string | number | null;
    y: string | number | null;
    series: string | null;
    isTemporal: boolean;
    tempGrain: SignalsTemporalLabels;
    startDate: moment.Moment;
    endDate: moment.Moment;
    isPercent?: boolean;
    title?: string;
    scoredConversationCount?: number;
    eligibleConversations?: { count: number; total: number };
}) => {
    const yValueFontSize = "34px";
    const yComponent = (
        <Typography
            style={{
                fontSize: yValueFontSize,
                color: theme.palette.blue.main,
            }}
        >
            {args.isPercent
                ? Number(args.y).toFixed(1) + "%"
                : Number(args.y).toLocaleString()}
        </Typography>
    );
    const subtitle = args.isTemporal
        ? getXDateRangeLabel(
              args.x,
              args.tempGrain,
              args.startDate,
              args.endDate,
          )
        : args.x;
    const title = args.title ?? (!!args.series ? args.series : subtitle);

    const titleComponent = (
        <Typography style={{ fontWeight: 600, fontSize: "14px" }}>
            {title}
        </Typography>
    );

    const subTitleComponent = !!args.series ? (
        <Typography style={{ fontSize: "14px" }}>
            {args.isTemporal
                ? getXDateRangeLabel(
                      args.x,
                      args.tempGrain,
                      args.startDate,
                      args.endDate,
                  )
                : args.x}
        </Typography>
    ) : null;

    const eligibleComponent = args.eligibleConversations ? (
        <Typography
            style={{ fontSize: "14px", color: theme.palette.gray[600] }}
        >
            {`${args.eligibleConversations.count.toLocaleString()}/${args.eligibleConversations.total.toLocaleString()} conversations`}
        </Typography>
    ) : null;

    const scoredCountComponent =
        args.scoredConversationCount != null ? (
            <Typography
                style={{ fontSize: "14px", color: theme.palette.gray[600] }}
            >
                {`${args.scoredConversationCount.toLocaleString()} scored ${
                    args.scoredConversationCount === 1
                        ? "conversation"
                        : "conversations"
                }`}
            </Typography>
        ) : null;

    return (
        <Grid>
            {titleComponent}
            {yComponent}
            {subTitleComponent}
            {scoredCountComponent}
            {eligibleComponent}
        </Grid>
    );
};
